import { useEffect, useMemo, useRef, useState } from 'react'
import { useVisitState } from '../../hooks/useTabVisit'
import { useSearchParams } from 'react-router-dom'
import { fbt, useLocaleContext } from 'fbtee'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import { getSavedFestivalRoute } from '../../lib/festivalModeRestore'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { APP_NAME } from '@shared/brand'
import { useEventLineups } from '../../hooks/useEventLineups'
import { useEventArtistMatches } from '../../hooks/useEventArtistMatches'
import { usePlansFeed } from '../../hooks/usePlansFeed'
import { track, authStateLabel } from '../../lib/analytics'
import { usePageLoadTrace } from '../../lib/perf'
import { getLineupNames } from '../../lib/eventUtils'
import { todayLocalISO } from '../../lib/formatDate'
import { agendaRowId, buildDayMarks } from '../../lib/eventCalendar'
import { centermostElement, holdScrollAnchor } from '../../lib/scrollAnchor'
import { useWeekStart } from '../../hooks/useWeekStart'
import { PageLayout } from '../../components/PageLayout'
import { ErrorState } from '../../components/ErrorState'
import { HeroEventCard } from '../../components/HeroEventCard'
import { EventAttendanceMenu } from '../../components/EventAttendanceMenu'
import { GroupUpsellCard } from '../../components/GroupUpsellCard'
import { Section } from '../../components/Section'
import { SocialEventCard } from '../../components/SocialEventCard'
import { PregameMark, PregameStrip } from '../../components/PregameStrip'
import { useMyPregames, usePregameGoerProfiles } from '../../hooks/usePregames'
import { useTicketMarksByEvent } from '../../hooks/useTicketMarksByEvent'
import { offersTicketsAndPregames, pregameGoingIds } from '@shared/pregames'
import { PlansCalendar } from '../../components/plans/PlansCalendar'
import { PlansAgenda } from '../../components/plans/PlansAgenda'
import { PlansDayList } from '../../components/plans/PlansDayList'
import { HeaderLink } from '../../components/HeaderLink'
import { RegionHeaderChip } from '../../components/RegionHeaderChip'
import { WelcomeHeroCard } from './WelcomeHeroCard'
import { RenameNotice } from '../../components/RenameNotice'
import { pickHomeHeroes } from '../../lib/heroEvents'
import { HomeSuggestions } from './HomeSuggestions'
import { HomeFabs } from './HomeFabs'
import { readFriendsPlans, writeFriendsPlans } from './friendsPlansPref'
import type { Event } from '../../types'
import { useMinuteNow } from '../../hooks/useMinuteNow'

// Its own chunk: the main bundle sits at the PWA precache size limit, and nothing
// renders here until the digest has loaded anyway.

// Restores festival mode if the user was in it when the app last closed. Read
// at render, so a boot that only passes through Home on its way back to
// festival mode never opens Home's listeners (~1k reads each time).
export default function HomePage() {
  const navigate = useTransitionNavigate()
  const [festivalRoute] = useVisitState(getSavedFestivalRoute)
  useEffect(() => {
    if (festivalRoute) navigate(festivalRoute, { replace: true })
  }, [festivalRoute, navigate])
  return festivalRoute ? null : <HomeContent />
}

function HomeContent() {
  useDocumentTitle(APP_NAME)
  const navigate = useTransitionNavigate()
  const openEvent = useOpenEvent()
  const [searchParams] = useSearchParams()
  const { locale } = useLocaleContext()

  // Older "now covers X" pushes land on `/?region=`; the region picker lives on Discover now.
  const regionParam = searchParams.get('region')
  useEffect(() => {
    if (regionParam) navigate(`/discover?region=${encodeURIComponent(regionParam)}`, { replace: true })
  }, [regionParam, navigate])

  // The FAB and calendar flip on tap; the agenda follows once crew rows have collapsed.
  const [crewShown, setCrewShown] = useState(readFriendsPlans)
  const [crewInAgenda, setCrewInAgenda] = useState(crewShown)
  const [leavingIds, setLeavingIds] = useState<Set<string> | undefined>()
  const [droppedIds, setDroppedIds] = useState<Set<string>>(() => new Set())
  const leaveTimer = useRef<number | undefined>(undefined)
  const feed = usePlansFeed()
  const {
    user, signedInUid, authLoading, interestsLoading, eventStatuses, guardedSetEventInterest,
    facepileData, mergedProfiles, currentUserId, groups, friendIds, socialLoading, crewEventIds,
    myEvents, friendEvents, pastMyEvents, pastFriendEvents, ready, loading, error,
  } = feed

  usePageLoadTrace('home_page_to_content', ready, { auth_state: authStateLabel(user) })
  const pregamesByEvent = useMyPregames(signedInUid)
  const profiles = usePregameGoerProfiles(pregamesByEvent, mergedProfiles)
  const ticketMarks = useTicketMarksByEvent(signedInUid, friendIds)

  const { weekStartsOn } = useWeekStart()
  const today = todayLocalISO()
  const now = useMinuteNow()

  // The hero leads the page, so the agenda skips it.
  const { top: heroEvent, inline: inlineHeroEvent } = useMemo(
    () => pickHomeHeroes(myEvents, eventStatuses, now),
    [myEvents, eventStatuses, now],
  )
  const agendaEvents = useMemo(
    () => [...myEvents, ...(crewInAgenda ? friendEvents : [])]
      .filter(e => e.id !== heroEvent?.id && !droppedIds.has(e.id))
      .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [myEvents, friendEvents, crewInAgenda, heroEvent, droppedIds],
  )
  // The calendar also pages back through past plans.
  const calendarMine = useMemo(() => [...myEvents, ...pastMyEvents], [myEvents, pastMyEvents])
  const calendarFriends = useMemo(() => [...friendEvents, ...pastFriendEvents], [friendEvents, pastFriendEvents])
  const myIds = useMemo(() => new Set(calendarMine.map(e => e.id)), [calendarMine])
  const eventsById = useMemo(() => new Map([...calendarMine, ...calendarFriends].map(e => [e.id, e])), [calendarMine, calendarFriends])
  // The lists are frozen but statuses are live, so ownership follows the status:
  // a friends' event the viewer just marked going is theirs now. A called-off
  // event stays in the agenda, struck through, but isn't a plan on the calendar.
  const marks = useMemo(() => {
    const mine: { event: Event; status: 'going' | 'interested' }[] = []
    const friends: Event[] = []
    const declined = new Set<string>()
    for (const event of [...calendarMine, ...calendarFriends]) {
      if (event.cancelled) continue
      const status = eventStatuses.get(event.id)
      if (status === 'going' || status === 'interested') mine.push({ event, status })
      else if (!myIds.has(event.id)) {
        friends.push(event)
        if (status === 'not_going') declined.add(event.id)
      }
    }
    return buildDayMarks(mine, friends, declined)
  }, [calendarMine, calendarFriends, eventStatuses, myIds])

  const partyEvents = useMemo(() => [...myEvents, ...friendEvents].filter(e => e.type === 'party'), [myEvents, friendEvents])
  const { lineups } = useEventLineups(partyEvents)
  const { matchedArtists, lineupSizes } = useEventArtistMatches(signedInUid ?? null)

  const isGuest = !signedInUid
  const hasPlans = myEvents.length > 0 || friendEvents.length > 0

  function toggleFriends(next: boolean) {
    setCrewShown(next)
    writeFriendsPlans(next)
    track('home_friends_plans_toggled', { enabled: next })
    window.clearTimeout(leaveTimer.current)
    // The freeze only shields the card being tapped; the filter re-applies it, so
    // plans the viewer has dropped since leave too, unless they're the crew's and
    // the crew is coming back into view.
    const dropped = new Set(myEvents
      .filter(e => !ownerOf(e) && !(next && crewEventIds.has(e.id)))
      .map(e => e.id))
    const leaving = new Set(agendaEvents
      .filter(e => dropped.has(e.id) || (!next && ownerOf(e) === 'friends'))
      .map(e => e.id))
    anchorCenterCard(leaving)
    if (next) setCrewInAgenda(true)
    setLeavingIds(leaving.size > 0 ? leaving : undefined)
    leaveTimer.current = window.setTimeout(() => {
      if (!next) setCrewInAgenda(false)
      setDroppedIds(dropped)
      setLeavingIds(undefined)
    }, 300)
  }

  // Scrolled down, rows growing or collapsing above the card being read would
  // shove it; hold the centermost staying card still and let the rest move around it.
  function anchorCenterCard(leaving: Set<string>) {
    if (window.scrollY < 80) return
    const leavingRows = new Set([...leaving].map(agendaRowId))
    const rows = [...document.querySelectorAll('[data-agenda-row]')]
      .filter(el => !leavingRows.has(el.id))
    const anchor = centermostElement(rows)
    if (anchor) holdScrollAnchor(anchor, 400)
  }

  function ownerOf(e: Event) {
    const status = eventStatuses.get(e.id)
    if (status === 'going' || status === 'interested') return status
    return myIds.has(e.id) ? undefined : 'friends'
  }

  function renderDayPlans(day: string) {
    const events = (marks.get(day)?.eventIds ?? []).flatMap(id => eventsById.get(id) ?? [])
    return (
      <PlansDayList day={day} events={events} locale={locale}
        ownerOf={e => (eventStatuses.get(e.id) === 'not_going' ? 'not_going' : ownerOf(e))}
        onOpen={e => openEvent(e, eventStatuses.get(e.id))}
        extraOf={pregameMark}
        onDiscover={day >= today ? () => discoverDay(day) : undefined} />
    )
  }

  // Festivals don't show tickets or pre-games, even from docs kept from before they stopped.
  const pregamesFor = (e: Event) => (offersTicketsAndPregames(e) ? pregamesByEvent.get(e.id) : undefined)

  function facepileFor(event: Event) {
    const tickets = offersTicketsAndPregames(event) ? ticketMarks.get(event.id) : undefined
    const face = facepileData.get(event.id) ?? (tickets && { userIds: [], goingUserIds: new Set<string>() })
    if (!face) return face
    const pregames = pregamesFor(event)
    return {
      ...face,
      pregameUserIds: pregames && new Set(pregames.flatMap(pregameGoingIds)),
      tickets,
    }
  }

  function pregameMark(e: Event) {
    const first = pregamesFor(e)?.[0]
    return first ? <PregameMark pregame={first} locale={locale} /> : null
  }

  function discoverDay(day: string) {
    track('home_calendar_discover_tapped', {})
    navigate(`/discover?date=${day}`, { replace: true, direction: 'none' })
  }

  function renderHero(event: Event, testId: string) {
    return (
      <div data-testid={testId}>
        <HeroEventCard
          event={event}
          today={today}
          lineup={getLineupNames(event, lineups)}
          followedArtists={matchedArtists.get(event.id)}
          lineupSize={lineupSizes.get(event.id)}
          onClick={() => openEvent(event, eventStatuses.get(event.id))}
          facepileLoading={socialLoading}
          facepile={facepileFor(event)}
          profiles={profiles}
          currentUserId={currentUserId}
          locale={locale}
          action={
            <EventAttendanceMenu
              variant="icon"
              status={eventStatuses.get(event.id) ?? 'none'}
              loading={authLoading || interestsLoading}
              onSetStatus={next => guardedSetEventInterest(event.id, next)}
              onDarkBackground={!!event.imageUrl}
            />
          }
          footer={
            <PregameStrip
              pregames={pregamesFor(event) ?? []}
              locale={locale}
            />
          }
        />
      </div>
    )
  }

  function renderCard(event: Event, onOpen?: () => void) {
    return (
      <SocialEventCard
        key={event.id}
        event={event}
        lineup={getLineupNames(event, lineups)}
        followedArtists={matchedArtists.get(event.id)}
        lineupSize={lineupSizes.get(event.id)}
        dateSuffix={pregameMark(event)}
        facepileLoading={socialLoading}
        facepile={facepileFor(event)}
        profiles={profiles}
        currentUserId={currentUserId}
        locale={locale}
        status={eventStatuses.get(event.id) ?? 'none'}
        statusLoading={authLoading || interestsLoading}
        onOpen={() => { onOpen?.(); openEvent(event, eventStatuses.get(event.id)) }}
        onSetStatus={next => guardedSetEventInterest(event.id, next)}
      />
    )
  }

  if (error) return <ErrorState error={error} />

  const exploreLink = (
    <HeaderLink onClick={() => navigate('/discover', { replace: true, direction: 'none' })}>
      {fbt('Explore', 'Link from the Home suggestions to the Discover tab')}
    </HeaderLink>
  )

  return (
    <PageLayout leading={<RegionHeaderChip />}>
      <RenameNotice />
      {/* Held until friends' plans resolve too, or a viewer with none of their own
          would flash an empty calendar before the welcome card. */}
      {loading || (!ready && myEvents.length === 0) ? (
        <>
          <PlansCalendar loading today={today} locale={locale} weekStartsOn={weekStartsOn} />
          <PlansAgenda loading today={today} weekStartsOn={weekStartsOn} />
        </>
      ) : !hasPlans ? (
        <>
          <WelcomeHeroCard
            variant={isGuest ? 'guest' : 'user'}
            onCta={() =>
              isGuest
                ? navigate('/login', { state: { from: { pathname: '/' } } })
                : navigate('/discover', { replace: true })
            }
          />
          <HomeSuggestions
            eventStatuses={eventStatuses}
            signedInUid={signedInUid ?? null}
            action={exploreLink}
            renderCard={renderCard}
          />
        </>
      ) : (
        <>
          {heroEvent && renderHero(heroEvent, 'home-hero')}
          <PlansCalendar marks={marks} today={today} locale={locale} weekStartsOn={weekStartsOn} crewHidden={!crewShown} settled={ready} renderDayPlans={renderDayPlans} />
          <PlansAgenda
            events={agendaEvents}
            today={today}
            weekStartsOn={weekStartsOn}
            leavingIds={leavingIds}
            ownerOf={ownerOf}
            renderCard={e => (e.id === inlineHeroEvent?.id ? renderHero(e, 'home-inline-hero') : renderCard(e))}
          />
        </>
      )}

      {ready && !isGuest && groups.length === 0 && (
        <Section title={
          <fbt desc="Section heading for the group upsell on the home page">
            Friends &amp; Groups
          </fbt>
        }>
          <GroupUpsellCard onClick={() => navigate('/social?create=group')} />
        </Section>
      )}

      {!loading && ready && !isGuest && (
        <HomeFabs withCrew={crewShown} onToggle={() => toggleFriends(!crewShown)} />
      )}
    </PageLayout>
  )
}
